import React from "react";
import { 
  Mail, 
  Phone, 
  MapPin, 
  Award, 
  Facebook, 
  ArrowRight,
  Handshake
} from "lucide-react";

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="relative overflow-hidden bg-gradient-to-br from-primary/5 via-white to-secondary/10">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-96 h-96 bg-primary rounded-full filter blur-3xl transform -translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-secondary rounded-full filter blur-3xl transform translate-x-1/2 translate-y-1/2"></div>
      </div>

      <div className="relative px-4 md:px-8 pt-16 pb-8">
        {/* Main Footer Content */}
        <div className="max-w-7xl mx-auto">
          {/* Top Section - Organization Highlight */}
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-primary mb-3">
              নিঃস্বার্থ নবজীবন সংগঠন
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              একটি অরাজনৈতিক, অলাভজনক, সামাজিক ও সেবামূলক সংগঠন
            </p>
          </div>

          {/* Main Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            {/* Philosophy Section */}
            <div className="lg:col-span-2 bg-white/70 backdrop-blur-sm rounded-2xl p-6 border border-white/50 shadow-lg">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 bg-primary/20 rounded-lg flex items-center justify-center">
                  <Award className="w-5 h-5 text-primary" />
                </div>
                <h3 className="text-xl font-bold text-primary">আমাদের দর্শন</h3>
              </div>
              <p className="text-gray-700 leading-relaxed mb-4">
                আমরা চাই সমাজের অসহায়, নিঃস্ব, হতদরিদ্র সকলেই যাতে তাদের মৌলিক অধিকার পায়, 
                সেই প্রচেষ্টায় আমরা এগিয়ে যাচ্ছি। সবাই মিলে একসাথে এই প্রচেষ্টায় সফলতায় পৌঁছাই—এই আমাদের লক্ষ্য।
              </p>
              <a
                href="/about"
                className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-white font-semibold px-6 py-3 rounded-lg transition-all duration-300 shadow-md hover:shadow-lg transform hover:scale-105"
              >
                বিস্তারিত জানুন
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>

            {/* Quick Links */}
            <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-6 border border-white/50 shadow-lg">
              <h3 className="text-lg font-bold text-primary mb-4 flex items-center gap-2">
                <div className="w-6 h-6 bg-secondary/20 rounded-md flex items-center justify-center">
                  <ArrowRight className="w-3 h-3 text-secondary" />
                </div>
                গুরুত্বপূর্ণ পেজ
              </h3>
              <nav className="space-y-3">
                {[
                  { name: "আমাদের সম্পর্কে", href: "/about" },
                  { name: "সদস্য তালিকা", href: "/member" },
                  { name: "মিডিয়া গ্যালারি", href: "/media/photo-gallery" },
                  { name: "ব্লাড ডোনেশন", href: "/blood-donors" },
                  { name: "প্রকল্পসমূহ", href: "/projects" },
                  { name: "যোগ দিন", href: "/join" }
                ].map((link, index) => (
                  <a
                    key={index}
                    href={link.href}
                    className="flex items-center gap-2 text-gray-700 hover:text-primary transition-colors duration-200 group"
                  >
                    <div className="w-1 h-1 bg-primary rounded-full group-hover:w-2 transition-all duration-200"></div>
                    {link.name}
                  </a>
                ))}
              </nav>
            </div>

            {/* Contact & Social */}
            <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-6 border border-white/50 shadow-lg">
              <h3 className="text-lg font-bold text-primary mb-4 flex items-center gap-2">
                <div className="w-6 h-6 bg-accent/20 rounded-md flex items-center justify-center">
                  <Handshake className="w-3 h-3 text-accent" />
                </div>
                যোগাযোগ
              </h3>
              
              {/* Contact Info */}
              <div className="space-y-4 mb-6">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                    <MapPin className="w-4 h-4 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-800">অস্থায়ী কার্যালয়</p>
                    <p className="text-sm text-gray-600">খলিফা পট্টি, আন্দরকিল্লা, চট্টগ্রাম</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-secondary/10 rounded-lg flex items-center justify-center">
                    <Phone className="w-4 h-4 text-secondary" />
                  </div>
                  <a
                    href="tel:01890227377"
                    className="text-sm text-gray-700 hover:text-secondary transition-colors duration-200 font-medium"
                  >
                    ০১৮৯০-২২৭৩৭৭
                  </a>
                </div>

                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-accent/10 rounded-lg flex items-center justify-center">
                    <Mail className="w-4 h-4 text-accent" />
                  </div>
                  <a
                    href="mailto:nnabajibon@gmail.com"
                    className="text-sm text-gray-700 hover:text-accent transition-colors duration-200 font-medium"
                  >
                    nnabajibon@gmail.com
                  </a>
                </div>
              </div>

              {/* Social Media */}
              <div>
                <h4 className="text-sm font-bold text-gray-800 mb-3">আমাদের সাথে থাকুন</h4>
                <div className="flex gap-3">
                  <a
                    href="https://www.facebook.com/nirshartha.nabojibon/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-primary hover:bg-primary/90 rounded-lg flex items-center justify-center transition-all duration-300 hover:scale-110 shadow-md"
                    aria-label="Facebook"
                  >
                    <Facebook className="w-5 h-5 text-white" />
                  </a>
                </div>
              </div>
            </div>
          </div>


          {/* Government Links */}
          <div className="bg-gray-50 rounded-xl p-6 mb-8">
            <h3 className="text-lg font-bold text-gray-800 mb-4 text-center">
              সরকারি গুরুত্বপূর্ণ লিংক
            </h3>
            <div className="flex flex-wrap justify-center gap-4">
              {[
                { name: "জেলা প্রশাসক, চট্টগ্রাম", href: "https://www.chittagong.gov.bd" },
                { name: "UNO অফিস", href: "https://www.chittagong.gov.bd/" },
                { name: "সমাজসেবা অধিদপ্তর", href: "https://dss.gov.bd/" }
              ].map((link, index) => (
                <a
                  key={index}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block bg-white hover:bg-primary/5 text-gray-700 hover:text-primary font-medium py-2 px-4 rounded-lg border border-gray-200 hover:border-primary/30 transition-colors duration-300 text-sm"
                >
                  {link.name}
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom Footer */}
        <div className="border-t border-gray-200 pt-8">
          <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="text-center md:text-left">
              <p className="text-gray-700 font-semibold">
                নিঃস্বার্থ নবজীবন সংগঠন (NNS)
              </p>
            </div>
            
            <div className="text-center md:text-right">
              <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
                <span>Designed & Developed By</span>
                <a
                  href="https://arifmia.netlify.app"
                  className="text-primary font-bold hover:underline transition-colors duration-200"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Md Arif
                </a>
              </div>
              <p className="text-sm text-gray-500">
                © 2017-{currentYear} সমস্ত অধিকার সংরক্ষিত
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;